<script setup lang="ts">
/**
 * NGワードのヒット箇所に下線マークを付けて本文を描画する。
 *
 * 従来の v-html + 文字列連結(highlightText)ではなく、セグメント配列を v-for で
 * 描画する形にしている。理由:
 *   - 重複ヒットの解決を文字列連結でやると壊れやすい
 *   - ホバー/クリックのイベントハンドラを素直に付けられる
 *   - 手動エスケープが不要になり、XSSの面でも安全
 *
 * 下線の色・線種はセグメントの最大レベルで決まる(レベルの高いものを隠さないため)。
 */
import { computed } from 'vue'
import type { NgWordMatch } from '@/types/api'
import { buildNgSegments, levelBucket } from '@/utils/ngHighlight'
import { useNgMarkStore } from '@/stores/ngMark'

const props = defineProps<{
  text: string | null | undefined
  ngWords: NgWordMatch[] | null | undefined
  /** 改行を保持して表示するか(特徴・商品説明はtrue、商品名はfalse) */
  preserveWhitespace?: boolean
  /**
   * getNgWords のフィールド名("title"/"model"/"color"/"size"/"features"/"description")。
   * 指定するとサーバーが返した正確なマッチ位置を使う。
   */
  field?: string
}>()

const ngMark = useNgMarkStore()

const segments = computed(() =>
  buildNgSegments(props.text, props.ngWords, ngMark.activeLevels, props.field),
)

function markClass(maxLevel: number) {
  return `ng-mark ng-mark-lv${levelBucket(maxLevel)}`
}

/** マーク要素の画面上の位置を、フローティングパネルの表示基準として渡す */
function rectOf(e: MouseEvent) {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  return { left: r.left, right: r.right, top: r.top, bottom: r.bottom }
}
function onEnter(hits: NgWordMatch[], e: MouseEvent) {
  ngMark.cancelClose()
  if (ngMark.pinned) return // 固定中はホバーで内容を変えない
  ngMark.setHovered(hits, rectOf(e))
}
function onClick(hits: NgWordMatch[], e: MouseEvent) {
  ngMark.togglePinned(hits, rectOf(e))
}

/** 固定中のマークかどうか(ハイライト表示に使う) */
function isPinned(hits: NgWordMatch[]) {
  const p = ngMark.pinned
  return !!p && p.length === hits.length && p.every((x, i) => x.word === hits[i].word)
}
</script>

<template>
  <span :class="{ 'ng-marked-text': true, 'preserve-ws': preserveWhitespace }">
    <template v-for="(seg, i) in segments" :key="i">
      <span
        v-if="seg.hits.length > 0"
        :class="[markClass(seg.maxLevel), { pinned: isPinned(seg.hits) }]"
        :data-ng-words="seg.hits.map((h) => h.word).join('\u001f')"
        @mouseenter="onEnter(seg.hits, $event)"
        @mouseleave="ngMark.scheduleClose()"
        @click="onClick(seg.hits, $event)"
      >{{ seg.text }}<sup v-if="seg.hits.length > 1" class="ng-mark-count">{{ seg.hits.length }}</sup></span>
      <template v-else>{{ seg.text }}</template>
    </template>
  </span>
</template>

<style scoped>
.preserve-ws {
  white-space: pre-wrap;
}
</style>
